import type { AppView } from '../../types';
import type { TutorialKapitel, TutorialKapitelId, TutorialRolle, TutorialSchritt } from '../../tutorial/types';

/**
 * LERNZENTRUM — die Rechnungen des Chats „Frag OS-IA" (Plan §1.8, Paket E).
 *
 * Rein: kein Supabase, kein React, kein `fetch`, kein DOM. Das Projekt
 * uebersetzt seine Tests ohne jsdom — eine Regel ist nur dann geprueft, wenn
 * sie HIER steht und nicht in der Komponente oder im Datenzugriff.
 *
 * Zwei Aufgaben, und beide sind der Grund, warum diese Datei ueberhaupt
 * existiert:
 *
 *   1. ENTSCHEIDEN, WAS MITGEHT. Der Chat-Kontext verlaesst das Haus. Er
 *      besteht ausschliesslich aus dem Register im Bundle (Titel,
 *      Bedienschritte, Stolperfallen) und aus freigegebenen Kapiteltexten —
 *      NIE aus Projektdaten. Es gibt hier keinen Pfad, auf dem ein Projekt,
 *      ein Bericht, ein Kunde oder eine Rechnung in den Kontext geraten
 *      koennte: die Funktion bekommt sie gar nicht erst gereicht.
 *
 *   2. DIE ANTWORT PRUEFEN. Was zurueckkommt, ist Text eines Sprachmodells
 *      und keine Zusage ueber ein Format. Eine erfundene Kapitel-ID wuerde in
 *      der Oberflaeche zu einem Knopf, der ins Leere fuehrt — deshalb
 *      ueberlebt nur, was im Register wirklich steht.
 */

// ═══════════════════════════════════════════════════════════════════════════
// 0) OBERGRENZEN
// ═══════════════════════════════════════════════════════════════════════════
//
// Gekappt wird HIER, an der Stelle, die die Kapitel kennt — nicht als blinder
// `slice` ueber fertiges JSON (das erzeugt unparsbares JSON mitten im Prompt).
// Der Proxy kappt ein zweites Mal; das ist die zweite Reihe gegen einen
// entgleisten Aufruf, nicht die eigentliche Begrenzung.
//
// Die Zahlen kommen aus dem Register: 20 Kapitel mit im Schnitt sieben
// Schritten. Zwoelf Kapitel sind mehr als die Haelfte des Stoffs und bleiben
// deutlich unter dem Token-Budget, das die ANTWORT braucht.

/** Kapitel je Anfrage (Plan §1.8: „max. ~12 Kapitel"). */
export const MAX_KONTEXT_KAPITEL = 12;
/** Bedienschritte je Kapitel. Der Rest des Kapitels steht im Lernzentrum. */
export const MAX_SCHRITTE_JE_KAPITEL = 8;
/** Stolperfallen je Kapitel — die kuratierte Liste ist selten laenger. */
export const MAX_STOLPERFALLEN_JE_KAPITEL = 5;
/** FAQ-Paare je Kapitel; sie kommen aus der Datenbank und nur freigegeben. */
export const MAX_FAQ_JE_KAPITEL = 3;
/** Zeichen je Schritt/Stolperfalle. Ein Stichwort ist ein Satz, kein Aufsatz. */
export const MAX_SCHRITT_ZEICHEN = 240;
/** Kapitel, auf die sich eine Antwort stuetzen darf. Mehr sind keine Antwort mehr. */
export const MAX_ANTWORT_KAPITEL = 3;
/** Zeichen der Antwort. Der Proxy bittet um sechs Saetze; das hier ist der Riegel. */
export const MAX_ANTWORT_ZEICHEN = 2000;
/** Zeichen der Frage. Deutlich unter dem CHECK auf `tutorial_fragen.frage` (4000). */
export const MAX_FRAGE_ZEICHEN = 1000;

/** Satz fuer die Oberflaeche, wenn der KI-Aufruf gar nicht durchkam. */
export const KI_NICHT_ERREICHBAR =
    'Die KI ist gerade nicht erreichbar. Die Kapitel im Lernzentrum stehen trotzdem vollständig zur Verfügung.';

/** Satz fuer eine Antwort, die zwar ankam, aber nichts Verwertbares enthielt. */
export const KEINE_HILFE_ANTWORT =
    'Darauf habe ich keine verwertbare Antwort bekommen. Bitte die Frage anders stellen — '
    + 'oder im Lernzentrum nachschlagen.';

// ═══════════════════════════════════════════════════════════════════════════
// 1) FORMEN
// ═══════════════════════════════════════════════════════════════════════════

export interface HilfeFaq {
    frage: string;
    antwort: string;
}

/** Ein Kapitel, so wie es an den Proxy geht. Bewusst schmal und flach. */
export interface HilfeKapitelKontext {
    id: TutorialKapitelId;
    titel: string;
    schritte: string[];
    stolperfallen: string[];
    faq?: HilfeFaq[];
}

/** Die geprueffte Antwort. `tourStart` fehlt, wenn keine Tour passt. */
export interface HilfeAntwort {
    antwort: string;
    kapitelIds: TutorialKapitelId[];
    tourStart?: TutorialKapitelId;
}

/**
 * Der Ausschnitt aus `tutorial_kapitel`, den der Chat braucht — strukturell
 * deckungsgleich mit `KapitelDaten` aus `tutorialService.ts`, aber HIER
 * nochmals als eigene Form beschrieben. Sonst zoege dieses reine Modul den
 * Supabase-Client in jeden Test.
 */
export interface HilfeDbInhalt {
    inhalt?: {
        schritte?: string[];
        stolperfallen?: string[];
        faq?: HilfeFaq[];
    };
}

// ═══════════════════════════════════════════════════════════════════════════
// 2) KLEINE HELFER
// ═══════════════════════════════════════════════════════════════════════════

/** Whitespace zusammenziehen und kappen. Zeilenumbrueche kosten nur Tokens. */
const text = (v: unknown, max: number = MAX_SCHRITT_ZEICHEN): string =>
    typeof v === 'string' ? v.replace(/\s+/g, ' ').trim().slice(0, max) : '';

const textliste = (v: unknown, anzahl: number, max: number = MAX_SCHRITT_ZEICHEN): string[] =>
    Array.isArray(v) ? v.map((e) => text(e, max)).filter((s) => !!s).slice(0, anzahl) : [];

/**
 * Schritte auf das eindampfen, was der Chat braucht: den Satz, der sagt, was
 * an dieser Stelle passiert.
 *
 * Nimmt beide Formen — `TutorialSchritt` aus dem Register (dann zaehlt
 * `stichwort`) und fertige Saetze aus `tutorial_kapitel.inhalt.schritte`. Der
 * Anker (`data-tutorial`) geht NICHT mit: er ist ein Implementierungsdetail
 * der Tour und hilft beim Beantworten einer Frage kein Stueck.
 *
 * Die Markdown-Fettung der kuratierten Stolperfallen (`**…**`) faellt weg —
 * sie ist Auszeichnung fuer die Kapitelseite, nicht Inhalt.
 */
export const kuerzeSchritte = (
    schritte: ReadonlyArray<TutorialSchritt | string> | undefined,
    anzahl: number = MAX_SCHRITTE_JE_KAPITEL,
    zeichen: number = MAX_SCHRITT_ZEICHEN,
): string[] => {
    if (!Array.isArray(schritte)) return [];
    return schritte
        .map((s) => {
            const roh = typeof s === 'string' ? s : (s as TutorialSchritt)?.stichwort;
            return text(typeof roh === 'string' ? roh.replace(/\*\*/g, '') : roh, zeichen);
        })
        .filter((s) => !!s)
        .slice(0, anzahl);
};

// ═══════════════════════════════════════════════════════════════════════════
// 3) KONTEXT BAUEN
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Die Kapitel, die mit einer Frage mitgehen.
 *
 * Die Reihenfolge ist Inhalt, nicht Kosmetik: das Modell liest von oben, und
 * die Kapitel der GERADE OFFENEN Ansicht beantworten die Frage in aller Regel
 * („wie buche ich das hier?"). Sie stehen deshalb vorn, der Rest des
 * rollensichtbaren Registers danach — abgeschnitten wird also immer hinten,
 * wo der Stoff am wenigsten zur Frage passt.
 *
 * Drei Entscheidungen, die alle drei Absicht sind:
 *
 *  - **Leere Rollenliste heisst leerer Kontext**, nicht „alles". Die
 *    Freelancer-Trennung (siehe `rollenAusProfil`) darf nicht daran haengen,
 *    dass ein Aufrufer eine Prop vergisst. Ein stummer Chat ist die harmlose
 *    Richtung; ein Chat, der einem Externen die Verwaltung erklaert, ist es
 *    nicht.
 *  - **Freigegebene Datenbanktexte gewinnen ueber das Register.** Steht in
 *    `tutorial_kapitel` ein ausformulierter Schritt, geht der mit; sonst das
 *    Stichwort aus dem Register. Ohne Migration und ohne Pipeline ist der
 *    Kontext also vollstaendig — nur knapper.
 *  - **Stolperfallen kommen aus BEIDEN Quellen** (Register und
 *    `stolperfallen.md`) und werden entdoppelt. Genau sie sind der Teil, den
 *    ein Nutzer nicht erraten kann und der eine Chat-Antwort erst nuetzlich
 *    macht.
 */
export const kontextFuerFrage = (
    view: AppView,
    rollen: TutorialRolle[],
    register: ReadonlyArray<TutorialKapitel>,
    stolperfallen: (id: TutorialKapitelId) => string[],
    dbInhalte?: ReadonlyMap<TutorialKapitelId, HilfeDbInhalt>,
    deckel: number = MAX_KONTEXT_KAPITEL,
): HilfeKapitelKontext[] => {
    if (!Array.isArray(register) || !Array.isArray(rollen) || rollen.length === 0) return [];

    const sichtbar = register.filter(
        (k) => !!k?.id && Array.isArray(k.rollen) && k.rollen.some((r) => rollen.includes(r)),
    );
    const zurView = sichtbar.filter((k) => k.view === view);
    const rest = sichtbar.filter((k) => k.view !== view);

    return [...zurView, ...rest].slice(0, Math.max(0, deckel)).map((k) => {
        const db = dbInhalte?.get(k.id);
        const ausDb = kuerzeSchritte(db?.inhalt?.schritte);
        const schritte = ausDb.length ? ausDb : kuerzeSchritte(k.schritte);

        const fallen = Array.from(new Set([
            ...kuerzeSchritte(k.stolperfallen, MAX_STOLPERFALLEN_JE_KAPITEL),
            ...kuerzeSchritte(db?.inhalt?.stolperfallen, MAX_STOLPERFALLEN_JE_KAPITEL),
            ...kuerzeSchritte(stolperfallen?.(k.id), MAX_STOLPERFALLEN_JE_KAPITEL),
        ])).slice(0, MAX_STOLPERFALLEN_JE_KAPITEL);

        const faq = (Array.isArray(db?.inhalt?.faq) ? (db!.inhalt!.faq as HilfeFaq[]) : [])
            .map((f) => ({ frage: text(f?.frage, 200), antwort: text(f?.antwort) }))
            .filter((f) => !!f.frage && !!f.antwort)
            .slice(0, MAX_FAQ_JE_KAPITEL);

        const eintrag: HilfeKapitelKontext = {
            id: k.id,
            titel: text(k.titel, 200),
            schritte,
            stolperfallen: fallen,
        };
        if (faq.length) eintrag.faq = faq;
        return eintrag;
    });
};

// ═══════════════════════════════════════════════════════════════════════════
// 4) ANTWORT PRUEFEN
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Rohe Modellantwort → geprueffte Struktur.
 *
 * `bekannteIds` sind die IDs, die es WIRKLICH gibt (das Register). Alles
 * andere fliegt raus — eine erfundene ID waere in der Oberflaeche ein Chip,
 * der auf eine leere Seite fuehrt, und ein „Tour starten", das nichts startet.
 *
 * Der Fall `{ text: … }` ist der Rueckfall des Proxys, wenn die Modellantwort
 * kein JSON war. Der Text ist dann brauchbar (es ist ja die Antwort), nur ohne
 * Kapitelbezug — besser, als ihn wegzuwerfen und „keine Antwort" zu melden.
 */
export const normalisiereAntwort = (roh: unknown, bekannteIds: Iterable<TutorialKapitelId>): HilfeAntwort => {
    const bekannt = bekannteIds instanceof Set
        ? (bekannteIds as Set<TutorialKapitelId>)
        : new Set<TutorialKapitelId>(bekannteIds ?? []);
    const leer: HilfeAntwort = { antwort: '', kapitelIds: [] };

    if (!roh || typeof roh !== 'object' || Array.isArray(roh)) return leer;
    const k = roh as Record<string, unknown>;

    const antwort = text(k.antwort, MAX_ANTWORT_ZEICHEN) || text(k.text, MAX_ANTWORT_ZEICHEN);
    if (!antwort) return leer;

    const kapitelIds = Array.from(new Set(textliste(k.kapitelIds, MAX_ANTWORT_KAPITEL * 3, 200)))
        .filter((id) => bekannt.has(id))
        .slice(0, MAX_ANTWORT_KAPITEL);

    // Eine Tour zu einem Kapitel, das die Antwort gar nicht nennt, ist kein
    // Fehler: das Modell darf auf das Kapitel zeigen, in dem der Ablauf steht.
    // Es muss nur existieren.
    const tourRoh = text(k.tourStart, 200);
    const tourStart = tourRoh && bekannt.has(tourRoh) ? tourRoh : undefined;

    return tourStart ? { antwort, kapitelIds, tourStart } : { antwort, kapitelIds };
};

/** Frage aufbereiten: Whitespace weg, gekappt. Leer heisst „nicht abschicken". */
export const normalisiereFrage = (frage: unknown): string => text(frage, MAX_FRAGE_ZEICHEN);
